import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { _checkRegistry } from '@/api/entity/mutations';
import useDebounce from '@/global-hooks/use-debounce';
import { PagePaths } from '@/routes/page-paths';
import { useEntitySlice } from '@/stores/selectors';
import { emitToast } from '@/utils/emit-toast';
import {
  getMatchingRegistryLockedFields,
  getRegistryJurisdiction,
  isPlausibleRegistryCompanyNumber,
  mapRegistryCompanyToEntityForm,
  RegistryFormMapping,
} from '@/utils/registry';

export type { RegistryCheckStatus } from '@/utils/registry';

export const useRegistryCheck = () => {
  const { pathname } = useLocation();
  const {
    stepForms,
    updateStepForm,
    registryLockedFields,
    setRegistryLockedFields,
    registryStatus: status,
    setRegistryStatus: setStatus,
  } = useEntitySlice();

  const [mapping, setMapping] = useState<RegistryFormMapping | null>(null);
  // Keyed by jurisdiction, not just number: switching country (GB <-> CH) must
  // re-run the check even when the typed number happens to stay the same.
  const lastCheckedRef = useRef<string | null>(null);
  const stepFormsRef = useRef(stepForms);
  stepFormsRef.current = stepForms;

  const { mutateAsync: checkRegistry } = useMutation({ mutationFn: _checkRegistry });

  const country = stepForms.country ?? null;
  const jurisdiction = getRegistryJurisdiction(country);
  const isSupported = jurisdiction !== null;
  const isManageEntity = pathname.includes(PagePaths.manageEntity);
  const companyNumber = stepForms.identificationNumber?.trim() ?? '';
  const debouncedNumber = useDebounce(companyNumber, 800);
  const checkKey = jurisdiction ? `${jurisdiction}:${debouncedNumber}` : null;

  const reset = useCallback(() => {
    setStatus('idle');
    setMapping(null);
    lastCheckedRef.current = null;
    if (registryLockedFields.length > 0) {
      setRegistryLockedFields([]);
    }
  }, [registryLockedFields.length, setRegistryLockedFields, setStatus]);

  const runCheck = useCallback(
    async (number: string) => {
      if (!jurisdiction || !country) return;
      const key = `${jurisdiction}:${number}`;
      lastCheckedRef.current = key;
      setStatus('checking');
      setMapping(null);

      try {
        const response = await checkRegistry({
          jurisdiction,
          companyNumber: number.toUpperCase(),
        });
        if (lastCheckedRef.current !== key) return;

        if (!response.found || !response.company) {
          setStatus('notFound');
          return;
        }

        setMapping(mapRegistryCompanyToEntityForm(response.company, country));
        setStatus('detected');
      } catch {
        if (lastCheckedRef.current !== key) return;
        setStatus('error');
        emitToast({
          title: "Couldn't check the public registry",
          message: 'Something went wrong. Please try again.',
          type: 'error',
          position: 'top-right',
        });
      }
    },
    [checkRegistry, country, jurisdiction, setStatus],
  );

  const restoreLockedFields = useCallback(
    async (number: string) => {
      if (!jurisdiction || !country) return;
      const key = `${jurisdiction}:${number}`;
      lastCheckedRef.current = key;

      try {
        const response = await checkRegistry({
          jurisdiction,
          companyNumber: number.toUpperCase(),
        });
        if (lastCheckedRef.current !== key) return;

        if (!response.found || !response.company) {
          setStatus('notFound');
          return;
        }

        const nextMapping = mapRegistryCompanyToEntityForm(response.company, country);
        const lockedFields = getMatchingRegistryLockedFields(nextMapping, stepFormsRef.current);
        if (!lockedFields.length) return;

        setRegistryLockedFields(lockedFields);
        setStatus('loaded');
      } catch {
        if (lastCheckedRef.current === key) setStatus('error');
      }
    },
    [checkRegistry, country, jurisdiction, setRegistryLockedFields, setStatus],
  );

  useEffect(() => {
    if (!isSupported || !isManageEntity) return;
    if (status !== 'idle' || registryLockedFields.length > 0) return;
    if (debouncedNumber !== companyNumber) return;
    if (!isPlausibleRegistryCompanyNumber(country, debouncedNumber)) return;
    if (checkKey === lastCheckedRef.current) return;

    restoreLockedFields(debouncedNumber);
  }, [
    isSupported,
    isManageEntity,
    status,
    registryLockedFields.length,
    debouncedNumber,
    companyNumber,
    country,
    checkKey,
    restoreLockedFields,
  ]);

  useEffect(() => {
    if (!isSupported || isManageEntity) return;
    if (status === 'checking' || status === 'detected' || status === 'loaded') return;
    if (debouncedNumber !== companyNumber) return;
    if (!isPlausibleRegistryCompanyNumber(country, debouncedNumber)) return;
    if (checkKey === lastCheckedRef.current) return;

    runCheck(debouncedNumber);
  }, [
    isSupported,
    isManageEntity,
    debouncedNumber,
    companyNumber,
    country,
    checkKey,
    status,
    runCheck,
  ]);

  const connect = useCallback(() => {
    if (!mapping) return;
    updateStepForm(mapping.updates);
    setRegistryLockedFields(mapping.lockedFields);
    setStatus('loaded');
  }, [mapping, updateStepForm, setRegistryLockedFields, setStatus]);

  const isVisible =
    isSupported &&
    (!isManageEntity || status === 'loaded' || status === 'notFound' || status === 'error');

  return {
    status,
    isSupported,
    isVisible,
    connect,
    reset,
  };
};
